import { useCallback, useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { setCapacitorAuthCookieFromLocalStorage } from '../../auth/capacitorAuthService';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';

/**
 * Hook that listens for app state changes or reloads and updates the auth cookie when appropriate.
 * This is necessary for capacitor apps as the auth cookie is not persisted between app sessions and may be expired early by safari.
 */
export default function () {
    const listenerRef = useRef<PluginListenerHandle | null>(null);

    const setupListener = useCallback(async () => {
        if (!Capacitor.isPluginAvailable('App')) return;

        // Fires just before app becomes active
        // Safari may consider the auth cookie third party as it is set client side, and so may expire it early.
        // This is to ensure the auth cookie is set again when the app returns to the foreground but doesn't reload.
        App.addListener('resume', setCapacitorAuthCookieFromLocalStorage).then((listener) => {
            listenerRef.current = listener;
        });
    }, []);

    useEffect(() => {
        // When the app is reloading, we want to set the auth cookie since a new session is starting
        setCapacitorAuthCookieFromLocalStorage();

        setupListener();

        return () => {
            listenerRef.current?.remove();
        };
    }, []);
}
